/**
 * Built-in Primitives Generator
 *
 * Generates GLB files for built-in primitive types (cube, sphere, plane, cylinder) during export.
 * Primitives are identified by a special `builtin:` prefix in their src path.
 */
import { Logger } from "../utils/Logger";
import { Scene } from "@babylonjs/core/scene";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import type { VertexData } from "@babylonjs/core/Meshes/mesh.vertexData";
import type { Material } from "@babylonjs/core/Materials/material";
import { GLTF2Export } from "@babylonjs/serializers";
import "@babylonjs/core/Meshes/Builders/boxBuilder";
import "@babylonjs/core/Meshes/Builders/sphereBuilder";
import "@babylonjs/core/Meshes/Builders/planeBuilder";
import "@babylonjs/core/Meshes/Builders/cylinderBuilder";
import { createDefaultData } from "../scenedata/schemas/defaults";
import type { BehaviorData } from "../scenedata/behaviordata";
import type { SceneObjectData } from "../scenedata/sceneobjectdata";

/** Prefix used to identify built-in primitives in src paths */
export const BUILTIN_PREFIX = "builtin:";

/** Supported primitive types */
export type PrimitiveType = "cube" | "sphere" | "plane" | "cylinder";

/** Map of primitive type to GLB filename */
export const PRIMITIVE_FILENAMES: Record<PrimitiveType, string> = {
    cube: "primitive_cube.glb",
    sphere: "primitive_sphere.glb",
    plane: "primitive_plane.glb",
    cylinder: "primitive_cylinder.glb",
};

/**
 * Check if a src path refers to a built-in primitive
 */
export function isBuiltInPrimitive(src: string | undefined): boolean {
    return src?.startsWith(BUILTIN_PREFIX) ?? false;
}

/**
 * Extract the primitive type from a builtin src path
 * @param src - The src path (e.g., "builtin:cube.glb")
 * @returns The primitive type, or null if not a valid builtin path
 */
export function getPrimitiveType(src: string): PrimitiveType | null {
    if (!isBuiltInPrimitive(src)) return null;

    const filename = src.substring(BUILTIN_PREFIX.length);
    for (const [type, expectedFilename] of Object.entries(PRIMITIVE_FILENAMES)) {
        if (filename === expectedFilename) {
            return type as PrimitiveType;
        }
    }
    return null;
}

/**
 * The primitive type behind a src this exporter wrote for a Shape: a live
 * `builtin:` src, or the saved file a save rewrites it to. That is
 * `./assets/primitive_<type>.glb` in a `.trace`/`.zip` ({@link getPrimitiveAssetPath})
 * and `files[k].uri` `assets/primitive_<type>.glb` in a folder document.
 *
 * A reopened save carries no other record of the built-in it came from, so
 * the name is the signal, and only these exact spellings count. The export
 * already owns them: a built-in anywhere in the scene is written under the
 * same name. `TraceExport.bakeBevelNodes` reads its Bevel host off the same
 * naming contract.
 */
export function getSavedPrimitiveType(src: string): PrimitiveType | null {
    const builtin = getPrimitiveType(src);
    if (builtin) return builtin;
    for (const [type, filename] of Object.entries(PRIMITIVE_FILENAMES)) {
        if (src === `./assets/${filename}` || src === `assets/${filename}`) {
            return type as PrimitiveType;
        }
    }
    return null;
}

/**
 * Get the builtin src path for a primitive type
 */
export function getBuiltInSrc(type: PrimitiveType): string {
    return `${BUILTIN_PREFIX}${PRIMITIVE_FILENAMES[type]}`;
}

/**
 * Get the asset filename for a primitive type (without builtin: prefix)
 */
export function getPrimitiveAssetPath(type: PrimitiveType): string {
    return `./assets/${PRIMITIVE_FILENAMES[type]}`;
}

/** How a built-in primitive's mesh is built: a MeshBuilder call, then an optional baked turn. */
export interface PrimitiveMeshParams {
    readonly builder: "CreateBox" | "CreateSphere" | "CreatePlane" | "CreateCylinder";
    /** The builder's options. Every primitive is one unit across. */
    readonly options: Readonly<Record<string, number>>;
    /** Degrees about X, baked into the vertices. */
    readonly rotateX?: number;
}

/** Frozen, options included: a host that writes to the table must not change the engine's primitives. */
const frozen = (params: PrimitiveMeshParams): PrimitiveMeshParams =>
    Object.freeze({ ...params, options: Object.freeze({ ...params.options }) });

/**
 * The built-in primitives' geometry. A host that rebuilds a primitive outside
 * the viewer (the sandbox bakes groups of them into one mesh) reads it from
 * here, so its copy is the same mesh the editor creates and the export writes.
 */
export const PRIMITIVE_MESH_PARAMS: Readonly<Record<PrimitiveType, PrimitiveMeshParams>> = Object.freeze({
    cube: frozen({ builder: "CreateBox", options: { size: 1 } }),
    sphere: frozen({ builder: "CreateSphere", options: { diameter: 1, segments: 32 } }),
    // Lies flat facing +Y rather than toward the camera (+Z).
    plane: frozen({ builder: "CreatePlane", options: { size: 1 }, rotateX: 90 }),
    cylinder: frozen({ builder: "CreateCylinder", options: { height: 1, diameter: 1, tessellation: 32 } }),
});

/**
 * Create a primitive mesh in the scene.
 * @param type - The primitive type to create
 * @param scene - The Babylon.js scene
 * @param name - Optional custom name for the mesh (defaults to "__builtin_{type}__")
 */
export function createPrimitiveMesh(type: PrimitiveType, scene: Scene, name?: string): Mesh {
    const { builder, options, rotateX } = PRIMITIVE_MESH_PARAMS[type];
    // A copy: the builders write their defaults into the options they are given.
    const mesh = MeshBuilder[builder](name ?? `__builtin_${type}__`, { ...options }, scene);
    if (rotateX !== undefined) {
        mesh.rotation.x = rotateX * Math.PI / 180;
        mesh.bakeCurrentTransformIntoVertices();
    }
    return mesh;
}

/**
 * Create a primitive mesh with a default PBR material.
 * @param type - The primitive type to create
 * @param scene - The Babylon.js scene
 * @param name - Base name for the mesh and material
 */
export function createPrimitiveWithMaterial(type: PrimitiveType, scene: Scene, name: string): Mesh {
    const mesh = createPrimitiveMesh(type, scene, `${name}_mesh`);

    const material = new PBRMaterial(`${name}_material`, scene);
    material.albedoColor = new Color3(0.8, 0.8, 0.8);
    material.metallic = 0.0;
    material.roughness = 0.5;
    mesh.material = material;

    return mesh;
}

/**
 * Generate a GLB blob for a primitive type
 * Creates a temporary mesh in an isolated scene, exports it to GLB, then cleans up.
 *
 * The isolated scene matters: GLTF2Export serializes every AnimationGroup in
 * the scene regardless of `shouldExportNode`, emitting channels whose target
 * node was filtered out (`target.node` undefined). Babylon ignores such
 * channels per spec, but Unity binds them to a fallback node — phantom
 * animation on the primitive. Exporting from a fresh scene (same pattern as
 * MotionExport.generateClipsGLB) guarantees no live-scene state leaks in.
 *
 * The UVs are written even though the placeholder material samples no
 * texture: Babylon drops UV sets the exported material does not use unless
 * `exportUnusedUVs` is set. The placeholder never lasts. A Renderer material
 * (a Material Library preset), a state variant or a Style puts a textured
 * material on the primitive, and on a mesh reopened without UVs every
 * texture renders as one flat colour.
 */
export async function generatePrimitiveGLB(type: PrimitiveType, scene: Scene): Promise<Blob> {
    const exportScene = new Scene(scene.getEngine());
    exportScene.useRightHandedSystem = scene.useRightHandedSystem;

    try {
        const mesh = createPrimitiveMesh(type, exportScene);

        // Create a basic PBR material for the primitive
        const material = new PBRMaterial(`__builtin_${type}_material__`, exportScene);
        material.albedoColor = new Color3(0.8, 0.8, 0.8);
        material.metallic = 0.0;
        material.roughness = 0.5;
        mesh.material = material;

        // Export just this mesh to GLB
        // Materials are automatically included when their mesh is exported.
        // The exact `node === mesh` allowlist (here and in generateGeometryGLB)
        // subsumes exportFilters.isEditorHelperMesh — no editor helper mesh can
        // exist in this scratch scene, and nothing but `mesh` may serialize.
        const glb = await GLTF2Export.GLBAsync(
            exportScene,
            PRIMITIVE_FILENAMES[type].replace(".glb", ""),
            {
                shouldExportNode: (node) => node === mesh,
                exportWithoutWaitingForScene: true,
                exportUnusedUVs: true,
            }
        );

        // Get the blob from the export result
        const glbFile = glb.glTFFiles[`${PRIMITIVE_FILENAMES[type].replace(".glb", "")}.glb`];
        if (glbFile instanceof Blob) {
            return glbFile;
        }
        // Handle case where it's returned as a different type
        return new Blob([glbFile], { type: "model/gltf-binary" });
    } finally {
        // Disposes the temporary mesh and material along with the scene
        exportScene.dispose();
    }
}

/**
 * Generate a GLB blob for arbitrary generated geometry (e.g. a baked Bevel
 * slab) with the same default PBR material as the primitives, or the one
 * `options.material` makes in the export scene. Same isolated-scene pattern
 * and the same kept UVs as generatePrimitiveGLB — see its JSDoc for why.
 */
export async function generateGeometryGLB(
    vertexData: VertexData,
    name: string,
    scene: Scene,
    options: { material?: (exportScene: Scene) => Material } = {},
): Promise<Blob> {
    const exportScene = new Scene(scene.getEngine());
    exportScene.useRightHandedSystem = scene.useRightHandedSystem;

    try {
        const mesh = new Mesh(name, exportScene);
        vertexData.applyToMesh(mesh);

        if (options.material) {
            mesh.material = options.material(exportScene);
        } else {
            const material = new PBRMaterial(`${name}_material`, exportScene);
            material.albedoColor = new Color3(0.8, 0.8, 0.8);
            material.metallic = 0.0;
            material.roughness = 0.5;
            mesh.material = material;
        }

        const glb = await GLTF2Export.GLBAsync(exportScene, name, {
            shouldExportNode: (node) => node === mesh,
            exportWithoutWaitingForScene: true,
            exportUnusedUVs: true,
        });

        const glbFile = glb.glTFFiles[`${name}.glb`];
        if (glbFile instanceof Blob) {
            return glbFile;
        }
        return new Blob([glbFile], { type: "model/gltf-binary" });
    } finally {
        exportScene.dispose();
    }
}

/**
 * Collect all unique primitive types used in the scene hierarchy, so the
 * export writes each one fresh.
 *
 * A reopened save names its Shapes by their saved file, and those regenerate
 * too. Carried over, the loaded file is whatever an older engine wrote: every
 * save before `exportUnusedUVs` had no UVs, so the Shape stayed flat under a
 * textured material however often it was saved again.
 */
export function collectUsedPrimitives(hierarchy: unknown[]): Set<PrimitiveType> {
    const usedPrimitives = new Set<PrimitiveType>();

    const scanObject = (obj: unknown) => {
        if (!obj || typeof obj !== "object") return;
        const record = obj as Record<string, unknown>;

        // Check src field
        if (typeof record.src === "string") {
            const type = getSavedPrimitiveType(record.src);
            if (type) {
                usedPrimitives.add(type);
            }
        }

        // Recursively scan all properties
        for (const key in record) {
            if (Object.hasOwn(record, key)) {
                const value = record[key];
                if (typeof value === "object") {
                    scanObject(value);
                }
            }
        }
    };

    hierarchy.forEach(node => scanObject(node));
    return usedPrimitives;
}

/**
 * Drop the Model a reopened Shape got on load, so a save without edits writes
 * what the first save wrote. A Shape is saved with no Model; the reopen loads
 * its saved file through the Model its src implies (`SceneCreation`'s "src as
 * template" rule), and the next load implies the same one again. A Model with
 * any field off its defaults was authored, and stays.
 */
export function dropImpliedPrimitiveModels(hierarchy: readonly SceneObjectData[]): void {
    const implied = new Map<string, unknown>(Object.entries(createDefaultData("Model")));
    const isImplied = (data: BehaviorData): boolean => {
        const entries = Object.entries(data);
        return entries.length === implied.size && entries.every(([key, value]) => implied.get(key) === value);
    };
    for (const record of hierarchy) {
        if (!record.src || !getSavedPrimitiveType(record.src) || !record.behaviors) continue;
        record.behaviors = record.behaviors.filter(data => !isImplied(data));
    }
}

/**
 * Rewrite builtin: src paths to local asset paths
 */
export function rewriteBuiltInPaths(hierarchy: unknown[]): void {
    const rewriteObject = (obj: unknown) => {
        if (!obj || typeof obj !== "object") return;
        const record = obj as Record<string, unknown>;

        // Rewrite src field
        if (typeof record.src === "string" && isBuiltInPrimitive(record.src)) {
            const type = getPrimitiveType(record.src);
            if (type) {
                const newPath = getPrimitiveAssetPath(type);
                Logger.log(`[BuiltInPrimitives] Rewriting src: ${record.src} -> ${newPath}`);
                record.src = newPath;
            }
        }

        // Recursively rewrite all properties
        for (const key in record) {
            if (Object.hasOwn(record, key)) {
                const value = record[key];
                if (typeof value === "object") {
                    rewriteObject(value);
                }
            }
        }
    };

    hierarchy.forEach(node => rewriteObject(node));
}
